/**
 * sceneStep.ts
 * Active scene step registry — selects which fixed-step function
 * `runPhysicsFrame()` invokes each tick.
 *
 * Phase 4 introduced World 2's space shmup, which cannot run the platformer
 * `stepPhysics()` body (gravity, Nova collision, ground friction etc.). Each
 * scene now registers its own fixed-step entry point. The platformer scene
 * (`PlayScene`) registers `stepPhysics`; the upcoming `SpaceScene` will
 * register its shmup step. The default remains `stepPhysics` so any code path
 * that runs the loop without an active scene (tests, initial boot) preserves
 * the pre-refactor behaviour.
 *
 * NO React imports, NO Reanimated imports, NO async — engine-internal only.
 *
 * @module sceneStep
 */

import { stepPhysics } from './physicsStep';

/** A fixed-step scene update. `dt` is always `FIXED_STEP` seconds. */
export type SceneStepFn = (dt: number) => void;

// DECISION: Default to the platformer step so tests that never call
// setActiveStep() (the majority — they pre-date the refactor) keep working
// without modification.
let _activeStep: SceneStepFn = stepPhysics;

/**
 * Register the active scene's fixed-step function.
 * Pass `null` to restore the default platformer step (used by `destroy()`).
 *
 * @param fn - The scene step to run each fixed tick, or `null` to reset.
 */
export function setActiveStep(fn: SceneStepFn | null): void {
  _activeStep = fn ?? stepPhysics;
}

/**
 * Invoke the currently-registered scene step for one fixed timestep.
 * Called from `runPhysicsFrame()`'s accumulator loop.
 *
 * @param dt - The fixed physics timestep in seconds (always FIXED_STEP).
 */
export function runActiveStep(dt: number): void {
  _activeStep(dt);
}
